import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Search, Plus, Loader2, Building2 } from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import ClientCard from '../../components/admin/ClientCard';
import ClientFilterChips from '../../components/admin/ClientFilterChips';

const ClientsPage = () => {
  const navigate = useNavigate();
  
  // === STATE ===
  const [clients, setClients] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Counts pour les filtres
  const [filterCounts, setFilterCounts] = useState({
    needs_action: 0,
    active: 0,
    inactive: 0,
    prospects: 0,
    all: 0
  });

  // === FETCH DATA ===
  useEffect(() => {
    fetchClients();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  useEffect(() => {
    if (allClients.length > 0) {
      applyFilter(activeFilter);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter, allClients]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      params.append('limit', '100');
      
      const response = await api.get(`/admin/clients?${params.toString()}`);
      const data = await response.json();
      
      const clientsList = data.clients || [];
      setAllClients(clientsList);
      
      // Calculate filter counts
      calculateFilterCounts(clientsList);
      
      // Apply current filter
      const filtered = applyFilterLogic(clientsList, activeFilter);
      setClients(filtered);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      toast.error('Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  };

  // === FILTER LOGIC ===
  const calculateFilterCounts = (clientsList) => {
    const counts = {
      all: clientsList.length,
      needs_action: clientsList.filter(c => c.needs_action).length,
      active: clientsList.filter(c => (c.missions_active || 0) > 0).length,
      inactive: clientsList.filter(c => {
        const daysInactive = c.days_inactive;
        return daysInactive && daysInactive > 30;
      }).length,
      prospects: clientsList.filter(c => (c.missions_total || 0) === 0).length
    };
    
    setFilterCounts(counts);
  };

  const applyFilterLogic = (clientsList, filter) => {
    switch (filter) {
      case 'needs_action':
        return clientsList.filter(c => c.needs_action);
        
      case 'active':
        return clientsList.filter(c => (c.missions_active || 0) > 0);
        
      case 'inactive':
        return clientsList.filter(c => {
          const daysInactive = c.days_inactive;
          return daysInactive && daysInactive > 30;
        });
        
      case 'prospects':
        return clientsList.filter(c => (c.missions_total || 0) === 0);
        
      default:
        return clientsList;
    }
  };

  const applyFilter = (filter) => {
    const filtered = applyFilterLogic(allClients, filter);
    
    // Sort by priority
    const sorted = [...filtered].sort((a, b) => {
      // 1. Needs action first
      if (a.needs_action && !b.needs_action) return -1;
      if (!a.needs_action && b.needs_action) return 1;
      
      // 2. By missions open count
      const aOpen = a.missions_open || 0;
      const bOpen = b.missions_open || 0;
      if (aOpen !== bOpen) return bOpen - aOpen;
      
      // 3. By missions active count
      const aActive = a.missions_active || 0;
      const bActive = b.missions_active || 0;
      if (aActive !== bActive) return bActive - aActive;
      
      // 4. By company name
      return (a.company_name || '').localeCompare(b.company_name || '');
    });
    
    setClients(sorted);
  };

  // === ACTIONS ===
  const handleViewMissions = useCallback((client) => {
    navigate(`/admin/missions?client_id=${client.id}`);
  }, [navigate]);

  const handleViewDetail = useCallback((client) => {
    navigate(`/admin/clients/${client.id}`);
  }, [navigate]);

  // === RENDER ===
  const clientCount = clients.length;
  const needsActionCount = filterCounts.needs_action;

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto" data-testid="clients-page">
      {/* === HEADER === */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-display font-semibold text-tp-text">
            Clients
          </h1>
          <p className="text-tp-text-light text-sm md:text-base">
            {needsActionCount > 0 
              ? `${needsActionCount} client${needsActionCount > 1 ? 's' : ''} en attente d'action`
              : `${clientCount} client${clientCount !== 1 ? 's' : ''}`
            }
          </p>
        </div>
        <Link to="/admin/clients/new">
          <Button 
            className="bg-tp-primary hover:bg-tp-primary-dark text-white"
            data-testid="add-client-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Nouveau</span>
          </Button>
        </Link>
      </div>

      {/* === FILTER CHIPS === */}
      <div className="mb-4">
        <ClientFilterChips
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
          counts={filterCounts}
        />
      </div>

      {/* === SEARCH === */}
      <Card className="p-4 mb-6 border-tp-border">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-tp-text-light" />
          <Input
            placeholder="Rechercher par nom, contact, email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-clients"
          />
        </div>
      </Card>

      {/* === CLIENTS LIST === */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-tp-text-light" />
        </div>
      ) : clients.length > 0 ? (
        <div className="space-y-4">
          {clients.map((client) => (
            <ClientCard
              key={client.id}
              client={client}
              onViewMissions={() => handleViewMissions(client)}
              onViewDetail={() => handleViewDetail(client)}
            />
          ))}
        </div>
      ) : (
        <Card className="p-12 text-center border-tp-border">
          <Building2 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-tp-text mb-2">
            {searchQuery || activeFilter !== 'all' 
              ? 'Aucun client trouvé'
              : 'Aucun client enregistré'
            }
          </h3>
          <p className="text-tp-text-light mb-4">
            {searchQuery || activeFilter !== 'all' 
              ? 'Aucun client ne correspond à votre recherche.'
              : 'Ajoutez votre premier client pour commencer.'
            }
          </p>
          {(searchQuery || activeFilter !== 'all') ? (
            <Button 
              variant="outline"
              onClick={() => {
                setSearchQuery('');
                setActiveFilter('all');
              }}
            >
              Réinitialiser les filtres
            </Button>
          ) : (
            <Link to="/admin/clients/new">
              <Button className="bg-tp-primary hover:bg-tp-primary-dark">
                <Plus className="h-4 w-4 mr-2" />
                Ajouter un client
              </Button>
            </Link>
          )}
        </Card>
      )}
    </div>
  );
};

export default ClientsPage;
